import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { FiTrendingDown, FiTrendingUp } from "react-icons/fi";
import type {
  Action,
  CampaignIntent,
  CampaignIntentStatus,
  DisplayMarketingCampaign,
} from "common/interfaces/KYDMarketing";
import { INTENT_TYPE_LABELS } from "common/interfaces/KYDMarketing";
import { getIntentPerformanceCategory } from "./intentPerformance";

const INTENT_TYPE_LABELS_FALLBACK: Record<string, string> = {
  PAUSE_ADSET: "Pause ad set",
  INCREASE_ADSET_BUDGET: "Increase budget",
  CONSOLIDATE_ADSETS: "Consolidate adsets",
  INFORM_CREATIVE_FATIGUE: "Creative fatigue",
};

const PRIMARY_TEXT_COLOR = "#2C3E50";
const REASONING_MAX_LENGTH = 80;

/** One-line description of a single action (e.g. "Pause ad set \"Summer Sale\""). */
function formatActionLine(action: Action): string {
  const label = action.label;
  if (label) return label;
  const verb =
    action.type === "PAUSE"
      ? "Pause"
      : action.type === "INCREASE_BUDGET"
        ? "Increase budget for"
        : action.type;
  const entity =
    action.entity_type === "adset"
      ? "ad set"
      : action.entity_type === "ad"
        ? "ad"
        : action.entity_type;
  const id = action.entity_id ? ` (id: ${action.entity_id})` : "";
  const extra =
    action.type === "INCREASE_BUDGET" &&
    (action as { step_pct?: number }).step_pct != null
      ? ` by ${(action as { step_pct: number }).step_pct}%`
      : "";
  return `${verb} ${entity}${id}${extra}`;
}

/** Full tooltip text: what will happen when the user clicks Apply. */
function buildApplyTooltip(intent: CampaignIntent): string {
  if (!intent.actions?.length) return "No actions to apply.";
  const lines = intent.actions.map((a) => `• ${formatActionLine(a)}`);
  return `Clicking Apply will:\n${lines.join("\n")}`;
}

/** Entity ids affected by this intent (e.g. adset ids for highlighting). */
function getAffectedEntityIds(intent: CampaignIntent): string[] {
  if (!intent.actions?.length) return [];
  return intent.actions.map((a) => a.entity_id).filter(Boolean);
}

/** Ad set id to scroll to for this intent (adset-level or ad-level). Null for campaign-only intents. */
function getAdsetIdForIntent(
  intent: CampaignIntent,
  campaign: DisplayMarketingCampaign | undefined
): string | null {
  const first = intent.actions?.[0];
  if (!first?.entity_id || !campaign) return null;
  const type = first.entity_type;
  const id = first.entity_id;
  const adsets = campaign.adsets ?? [];
  if (type === "adset") return id;
  if (type === "ad") {
    const adset = adsets.find((a) =>
      a.ads?.some((ad) => ad.id === id || ad.external_id === id)
    );
    return adset?.id ?? null;
  }
  return null;
}

function fallbackEntityName(intent: CampaignIntent): string {
  const label = (intent.actions?.[0] as { label?: string } | undefined)?.label;
  if (label && label.length <= 60) return label;
  const summary = intent.explanation?.summary ?? intent.reason ?? "";
  if (summary && summary.length <= 60) return summary;
  return "Ad set";
}

/**
 * First-line entity label: ad set name, or "Ad set name -- Ad name" for ad-level intents.
 * Resolves from campaign when available; otherwise falls back to action label / summary.
 */
function getIntentEntityName(
  campaign: DisplayMarketingCampaign | undefined,
  intent: CampaignIntent
): string {
  const first = intent.actions?.[0];
  if (!first?.entity_id) return fallbackEntityName(intent);

  const type = first.entity_type;
  const id = first.entity_id;
  const adsets = campaign?.adsets ?? [];

  if (type === "adset") {
    const adset = adsets.find((a) => a.id === id || a.external_id === id);
    return adset?.name ?? first.label ?? "Ad set";
  }

  if (type === "ad") {
    for (const adset of adsets) {
      const ad = adset.ads?.find((a) => a.id === id || a.external_id === id);
      if (ad) {
        const adName = ad.name ?? id;
        return `${adset.name} — ${adName}`;
      }
    }
    return first.label ?? "Ad";
  }

  if (type === "campaign") {
    return (campaign as { name?: string })?.name ?? first.label ?? "Campaign";
  }

  return fallbackEntityName(intent);
}

/** Short justification for the intent (why we suggest this action). Shown before the action label. */
function getIntentReasoning(intent: CampaignIntent): string {
  const short = intent.explanation?.short_summary;
  if (short) return short;
  const raw = intent.explanation?.summary ?? intent.reason ?? "";
  if (!raw) return "";
  if (raw.length <= REASONING_MAX_LENGTH) return raw;
  const truncated = raw.slice(0, REASONING_MAX_LENGTH).trim();
  const lastSpace = truncated.lastIndexOf(" ");
  return lastSpace > 40 ? truncated.slice(0, lastSpace) + "…" : truncated + "…";
}

/** Full explanation for tooltip (why we suggest this action). */
function getFullExplanation(intent: CampaignIntent): string {
  return intent.explanation?.summary ?? intent.reason ?? "";
}

/** Action label for the secondary line (e.g. "Increase budget", "Pause Ad Set"). */
function getActionLabel(intent: CampaignIntent): string {
  return (
    INTENT_TYPE_LABELS[intent.intent_type as keyof typeof INTENT_TYPE_LABELS] ??
    INTENT_TYPE_LABELS_FALLBACK[intent.intent_type] ??
    intent.intent_type.replace(/_/g, " ").toLowerCase()
  );
}

/** Only SUGGESTED (or undefined) intents show the Apply button. */
function isApplicable(intent: CampaignIntent): boolean {
  if (!intent.actionable) return false;
  const s = intent.status as CampaignIntentStatus | undefined;
  return s === "SUGGESTED" || s === undefined;
}

/** Derive performance (good vs bad) from intent type for arrow/colors. */
function getIntentPerformance(
  _campaign: DisplayMarketingCampaign | undefined,
  intent: CampaignIntent
): "performing_well" | "underperforming" | undefined {
  if (!_campaign || !intent.actionable) return undefined;
  const cat = getIntentPerformanceCategory(intent);
  return cat === "positive"
    ? "performing_well"
    : cat === "negative"
      ? "underperforming"
      : undefined;
}

export interface CampaignIntentItemProps {
  intent: CampaignIntent;
  index: number;
  campaign?: DisplayMarketingCampaign;
  isApplying: boolean;
  isIgnoring?: boolean;
  onApply: (e: React.MouseEvent, intent: CampaignIntent) => void;
  onDismiss: (e: React.MouseEvent, intent: CampaignIntent) => void;
  onApplyButtonHover?: (entityIds: string[]) => void;
  onApplyButtonLeave?: () => void;
  /** When set, clicking the insight scrolls the page to the corresponding ad set card. */
  onScrollToAdset?: (adsetId: string) => void;
}

const CampaignIntentItem: React.FC<CampaignIntentItemProps> = ({
  intent,
  index,
  campaign,
  isApplying,
  isIgnoring = false,
  onApply,
  onDismiss,
  onApplyButtonHover,
  onApplyButtonLeave,
  onScrollToAdset,
}) => {
  const applicable = isApplicable(intent);
  const applyTooltip = buildApplyTooltip(intent);
  const affectedIds = getAffectedEntityIds(intent);
  const performance = getIntentPerformance(campaign, intent);
  const entityName = getIntentEntityName(campaign, intent);
  const actionLabel = getActionLabel(intent);
  const adsetId = getAdsetIdForIntent(intent, campaign);
  const canScrollToAdset = Boolean(onScrollToAdset && adsetId);

  const reasoning = getIntentReasoning(intent);
  const fullExplanation = getFullExplanation(intent);
  const tooltipLabel = canScrollToAdset
    ? fullExplanation
      ? `${fullExplanation}\n\nClick to scroll to ad set.`
      : "Click to scroll to ad set."
    : fullExplanation;
  const variant =
    performance === "performing_well"
      ? ("positive" as const)
      : performance === "underperforming"
        ? ("negative" as const)
        : ("neutral" as const);
  const iconBg = variant === "positive" ? "#17A34A1A" : "#A317171A";
  const iconColor = variant === "positive" ? "#166434" : "#991B1B";
  const secondaryLine = reasoning
    ? `${reasoning} • `
    : variant === "positive"
      ? "Performing well • "
      : variant === "negative"
        ? "Underperforming • "
        : "";

  const handleScrollToAdset = () => {
    if (canScrollToAdset && adsetId) onScrollToAdset(adsetId);
  };

  return (
    <Box>
      {index > 0 && <Box borderTop="1px solid" borderColor="gray.200" mx={4} />}
      <HStack
        align="flex-start"
        justify="space-between"
        p={3}
        spacing={4}
        gap={3}
      >
        <HStack align="center" spacing={4} flex={1} minW={0}>
          <Box
            borderRadius="lg"
            bg={iconBg}
            p={2.5}
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon
              as={variant === "positive" ? FiTrendingUp : FiTrendingDown}
              boxSize={5}
              color={iconColor}
            />
          </Box>
          <Box
            flex={1}
            minW={0}
            onClick={canScrollToAdset ? handleScrollToAdset : undefined}
            cursor={canScrollToAdset ? "pointer" : undefined}
            _hover={canScrollToAdset ? { opacity: 0.9 } : undefined}
          >
            <VStack
              align="flex-start"
              justifyContent="flex-end"
              gap={0.5}
              minW={0}
            >
              <Text
                fontSize="12px"
                fontWeight="500"
                lineHeight="100%"
                letterSpacing="0.01em"
                verticalAlign="middle"
                textTransform="uppercase"
                color="gray.600"
                noOfLines={1}
              >
                {entityName}
              </Text>
              <Tooltip
                label={tooltipLabel}
                hasArrow
                placement="top"
                maxW="360px"
                whiteSpace="pre-wrap"
                isDisabled={!tooltipLabel}
              >
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  lineHeight="130%"
                  letterSpacing="0"
                  verticalAlign="middle"
                  color="gray.600"
                  cursor={
                    canScrollToAdset
                      ? "pointer"
                      : fullExplanation
                        ? "help"
                        : undefined
                  }
                >
                  {secondaryLine && (
                    <Text as="span" fontWeight="600">
                      {secondaryLine}
                    </Text>
                  )}
                  <Text as="span">{actionLabel}</Text>
                </Text>
              </Tooltip>
            </VStack>
          </Box>
        </HStack>
        <HStack flexShrink={0} spacing={2}>
          {applicable && (
            <Tooltip
              label={applyTooltip}
              hasArrow
              placement="left"
              whiteSpace="pre-wrap"
              maxW="320px"
            >
              <Button
                size="sm"
                variant="outline"
                bg="white"
                minW="100px"
                w="auto"
                h="40px"
                borderRadius="8px"
                borderWidth="1px"
                borderColor="#E5E7EB"
                boxShadow="0px 4px 10px 0px #0000001A"
                color={PRIMARY_TEXT_COLOR}
                fontWeight="500"
                opacity={1}
                gap="10px"
                px="12px"
                py="10px"
                whiteSpace="nowrap"
                onClick={(e) => onApply(e, intent)}
                isDisabled={isApplying}
                isLoading={isApplying}
                loadingText="Applying…"
                spinner={<Spinner size="sm" />}
                onMouseEnter={() => onApplyButtonHover?.(affectedIds)}
                onMouseLeave={() => onApplyButtonLeave?.()}
              >
                Apply
              </Button>
            </Tooltip>
          )}
          <IconButton
            aria-label={isIgnoring ? "Ignoring…" : "Dismiss"}
            icon={
              isIgnoring ? <Spinner size="sm" /> : <CloseIcon boxSize={3} />
            }
            size="sm"
            variant="outline"
            w="40px"
            h="40px"
            borderRadius="md"
            borderWidth="1px"
            borderColor="#E5E7EB"
            color={PRIMARY_TEXT_COLOR}
            bg="white"
            _hover={
              isIgnoring
                ? undefined
                : { bg: "gray.50", borderColor: "gray.400" }
            }
            onClick={(e) => !isIgnoring && onDismiss(e, intent)}
            isDisabled={isIgnoring}
          />
        </HStack>
      </HStack>
    </Box>
  );
};

export default CampaignIntentItem;
